import React, { useState, useEffect } from 'react';
import { superadminAPI, moduloAPI } from '../lib/api';
import { formatDate, getRolLabel } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { ScrollArea, ScrollBar } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
    Building2,
    Users,
    Package,
    Plus,
    Settings,
    ArrowLeft,
    Loader2,
    RefreshCw,
    Edit,
    CheckCircle,
    Shield,
    Trash2,
} from 'lucide-react';

export default function SuperAdminPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [empresas, setEmpresas] = useState([]);
    const [usuarios, setUsuarios] = useState([]);
    const [modulos, setModulos] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Dialog states
    const [showEmpresaDialog, setShowEmpresaDialog] = useState(false);
    const [showUsuarioDialog, setShowUsuarioDialog] = useState(false);
    const [showModulosDialog, setShowModulosDialog] = useState(false);
    const [selectedEmpresa, setSelectedEmpresa] = useState(null);
    const [processing, setProcessing] = useState(false);

    // Form states
    const [empresaForm, setEmpresaForm] = useState({
        nombre: '', rut: '', subdominio: '', primary_color: '#F97316'
    });
    const [usuarioForm, setUsuarioForm] = useState({
        email: '', nombre: '', password: '', rol: 'admin', empresa_id: ''
    });
    const [modulosSeleccionados, setModulosSeleccionados] = useState([]);

    useEffect(() => {
        if (user?.rol !== 'superadmin') {
            navigate('/home');
            return;
        }
        loadData();
    }, [user]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [empresasRes, usuariosRes, modulosRes] = await Promise.all([
                superadminAPI.getEmpresas(),
                superadminAPI.getUsuarios(),
                moduloAPI.getAll(),
            ]);
            setEmpresas(empresasRes.data);
            setUsuarios(usuariosRes.data);
            setModulos(modulosRes.data);
        } catch (err) {
            console.error('Error loading data:', err);
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateEmpresa = async () => {
        if (!empresaForm.nombre || !empresaForm.rut || !empresaForm.subdominio) {
            toast.error('Complete todos los campos requeridos');
            return;
        }

        setProcessing(true);
        try {
            await superadminAPI.createEmpresa({
                ...empresaForm,
                modulos_asignados: [],
                motivos_rechazo: [
                    'Sello roto',
                    'Carga incompleta',
                    'Documentación incorrecta',
                    'Vehículo no autorizado',
                    'Personal no identificado'
                ],
                nombre_area_seguridad: 'Protección Industrial',
            });
            toast.success('Empresa creada');
            setShowEmpresaDialog(false);
            setEmpresaForm({ nombre: '', rut: '', subdominio: '', primary_color: '#F97316' });
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al crear empresa');
        } finally {
            setProcessing(false);
        }
    };

    const handleCreateUsuario = async () => {
        if (!usuarioForm.email || !usuarioForm.nombre || !usuarioForm.password) {
            toast.error('Complete todos los campos requeridos');
            return;
        }

        setProcessing(true);
        try {
            await superadminAPI.createUsuario(usuarioForm);
            toast.success('Usuario creado');
            setShowUsuarioDialog(false);
            setUsuarioForm({ email: '', nombre: '', password: '', rol: 'admin', empresa_id: '' });
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al crear usuario');
        } finally {
            setProcessing(false);
        }
    };

    const openModulosDialog = (empresa) => {
        setSelectedEmpresa(empresa);
        setModulosSeleccionados(empresa.modulos_asignados || []);
        setShowModulosDialog(true);
    };

    const toggleModulo = (codigo) => {
        setModulosSeleccionados(prev =>
            prev.includes(codigo)
                ? prev.filter(c => c !== codigo)
                : [...prev, codigo]
        );
    };

    const handleAsignarModulos = async () => {
        if (!selectedEmpresa) return;

        setProcessing(true);
        try {
            await moduloAPI.asignar(selectedEmpresa.id, modulosSeleccionados);
            toast.success('Módulos asignados');
            setShowModulosDialog(false);
            setSelectedEmpresa(null);
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al asignar módulos');
        } finally {
            setProcessing(false);
        }
    };

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 bg-card/80 backdrop-blur-sm border-b-2 border-border h-16">
                <div className="max-w-7xl mx-auto h-full px-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate('/home')}
                            data-testid="back-btn"
                        >
                            <ArrowLeft className="h-5 w-5" />
                        </Button>
                        <div className="flex items-center gap-2">
                            <Shield className="h-6 w-6 text-primary" />
                            <h1 className="font-heading font-black text-xl uppercase tracking-tight">
                                Super Admin
                            </h1>
                        </div>
                    </div>
                    <Button
                        variant="outline"
                        onClick={loadData}
                        className="border-2"
                        data-testid="refresh-btn"
                    >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Actualizar
                    </Button>
                </div>
            </header>

            {/* Main Content */}
            <main className="pt-24 pb-12 px-4">
                <div className="max-w-7xl mx-auto">
                    <Tabs defaultValue="empresas">
                        <TabsList className="grid w-full grid-cols-3 mb-6">
                            <TabsTrigger value="empresas" className="uppercase text-xs tracking-wider font-bold">
                                <Building2 className="h-4 w-4 mr-2" />
                                Empresas ({empresas.length})
                            </TabsTrigger>
                            <TabsTrigger value="usuarios" className="uppercase text-xs tracking-wider font-bold">
                                <Users className="h-4 w-4 mr-2" />
                                Usuarios ({usuarios.length})
                            </TabsTrigger>
                            <TabsTrigger value="modulos" className="uppercase text-xs tracking-wider font-bold">
                                <Package className="h-4 w-4 mr-2" />
                                Módulos ({modulos.length})
                            </TabsTrigger>
                        </TabsList>

                        {/* Empresas Tab */}
                        <TabsContent value="empresas">
                            <Card className="border-2">
                                <CardHeader className="card-industrial-header flex-row items-center justify-between">
                                    <CardTitle>Empresas Registradas</CardTitle>
                                    <Button
                                        onClick={() => setShowEmpresaDialog(true)}
                                        className="btn-industrial bg-primary text-primary-foreground"
                                        data-testid="new-empresa-btn"
                                    >
                                        <Plus className="h-4 w-4 mr-2" />
                                        Nueva Empresa
                                    </Button>
                                </CardHeader>
                                <ScrollArea className="w-full">
                                    <Table className="table-industrial">
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Empresa</TableHead>
                                                <TableHead>Subdominio</TableHead>
                                                <TableHead>RUT</TableHead>
                                                <TableHead>Módulos</TableHead>
                                                <TableHead>Fecha</TableHead>
                                                <TableHead className="text-right">Acciones</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {loading ? (
                                                [1, 2, 3].map(i => (
                                                    <TableRow key={i}>
                                                        <TableCell><Skeleton className="h-5 w-32" /></TableCell>
                                                        <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                                                        <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                                                        <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                                                        <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                                                        <TableCell><Skeleton className="h-8 w-20 ml-auto" /></TableCell>
                                                    </TableRow>
                                                ))
                                            ) : empresas.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                                                        No hay empresas registradas
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                empresas.map(empresa => (
                                                    <TableRow key={empresa.id}>
                                                        <TableCell className="font-medium">{empresa.nombre}</TableCell>
                                                        <TableCell>
                                                            <Badge variant="outline" className="font-mono">
                                                                {empresa.subdominio}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell>{empresa.rut}</TableCell>
                                                        <TableCell>
                                                            <Badge variant="secondary">
                                                                {empresa.modulos_asignados?.length || 0} módulos
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell className="text-muted-foreground text-sm">
                                                            {formatDate(empresa.created_at)}
                                                        </TableCell>
                                                        <TableCell className="text-right">
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => openModulosDialog(empresa)}
                                                                className="border-2"
                                                                data-testid={`assign-modules-${empresa.subdominio}`}
                                                            >
                                                                <Package className="h-4 w-4 mr-2" />
                                                                Módulos
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                    <ScrollBar orientation="horizontal" />
                                </ScrollArea>
                            </Card>
                        </TabsContent>

                        {/* Usuarios Tab */}
                        <TabsContent value="usuarios">
                            <Card className="border-2">
                                <CardHeader className="card-industrial-header flex-row items-center justify-between">
                                    <CardTitle>Usuarios del Sistema</CardTitle>
                                    <Button
                                        onClick={() => setShowUsuarioDialog(true)}
                                        className="btn-industrial bg-primary text-primary-foreground"
                                        data-testid="new-usuario-btn"
                                    >
                                        <Plus className="h-4 w-4 mr-2" />
                                        Nuevo Usuario
                                    </Button>
                                </CardHeader>
                                <ScrollArea className="w-full">
                                    <Table className="table-industrial">
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Nombre</TableHead>
                                                <TableHead>Email</TableHead>
                                                <TableHead>Rol</TableHead>
                                                <TableHead>Empresa</TableHead>
                                                <TableHead>Fecha</TableHead>
                                                <TableHead className="text-right">Acciones</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {loading ? (
                                                [1, 2, 3, 4].map(i => (
                                                    <TableRow key={i}>
                                                        <TableCell><Skeleton className="h-5 w-32" /></TableCell>
                                                        <TableCell><Skeleton className="h-5 w-40" /></TableCell>
                                                        <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                                                        <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                                                        <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                                                    </TableRow>
                                                ))
                                            ) : usuarios.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                                                        No hay usuarios registrados
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                usuarios.map(usuario => {
                                                    const empresa = empresas.find(e => e.id === usuario.empresa_id);
                                                    return (
                                                        <TableRow key={usuario.id}>
                                                            <TableCell className="font-medium">{usuario.nombre}</TableCell>
                                                            <TableCell className="text-muted-foreground">{usuario.email}</TableCell>
                                                            <TableCell>
                                                                <Badge variant="outline" className="uppercase text-xs">
                                                                    {getRolLabel(usuario.rol)}
                                                                </Badge>
                                                            </TableCell>
                                                            <TableCell>
                                                                {empresa?.nombre || (usuario.rol === 'superadmin' ? '-' : 'Sin asignar')}
                                                            </TableCell>
                                                            <TableCell className="text-muted-foreground text-sm">
                                                                {formatDate(usuario.created_at)}
                                                            </TableCell>
                                                            <TableCell className="text-right">
                                                                {usuario.rol !== 'superadmin' && (
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                                                                        onClick={async () => {
                                                                            if (!window.confirm(`¿Eliminar usuario "${usuario.nombre}" (${usuario.email})?`)) return;
                                                                            try {
                                                                                await superadminAPI.deleteUsuario(usuario.id);
                                                                                toast.success(`Usuario "${usuario.nombre}" eliminado`);
                                                                                loadData();
                                                                            } catch (e) {
                                                                                toast.error(e.message || 'Error eliminando usuario');
                                                                            }
                                                                        }}
                                                                        data-testid={`btn-delete-user-${usuario.id}`}
                                                                    >
                                                                        <Trash2 className="h-4 w-4" />
                                                                    </Button>
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })
                                            )}
                                        </TableBody>
                                    </Table>
                                    <ScrollBar orientation="horizontal" />
                                </ScrollArea>
                            </Card>
                        </TabsContent>

                        {/* Módulos Tab */}
                        <TabsContent value="modulos">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {modulos.map(modulo => (
                                    <Card key={modulo.id} className="border-2">
                                        <CardContent className="p-6">
                                            <div className="flex items-center gap-3 mb-3">
                                                <div className="p-3 rounded-sm bg-primary/10">
                                                    <Package className="h-6 w-6 text-primary" />
                                                </div>
                                                <div>
                                                    <h3 className="font-heading font-bold uppercase">{modulo.nombre}</h3>
                                                    <p className="text-xs text-muted-foreground font-mono">{modulo.codigo}</p>
                                                </div>
                                            </div>
                                            <p className="text-sm text-muted-foreground mb-3">{modulo.descripcion}</p>
                                            <div className="flex items-center justify-between">
                                                <Badge variant={modulo.activo ? 'default' : 'secondary'}>
                                                    {modulo.activo ? 'Activo' : 'Inactivo'}
                                                </Badge>
                                                <span className="text-xs text-muted-foreground">{modulo.ruta_base}</span>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        </TabsContent>
                    </Tabs>
                </div>
            </main>

            {/* Create Empresa Dialog */}
            <Dialog open={showEmpresaDialog} onOpenChange={setShowEmpresaDialog}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-heading uppercase">Nueva Empresa</DialogTitle>
                        <DialogDescription>Complete los datos de la nueva empresa</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label className="input-label">Nombre</Label>
                            <Input
                                value={empresaForm.nombre}
                                onChange={(e) => setEmpresaForm({ ...empresaForm, nombre: e.target.value })}
                                placeholder="Nombre de la empresa"
                                className="input-industrial"
                                data-testid="empresa-nombre"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="input-label">RUT</Label>
                            <Input
                                value={empresaForm.rut}
                                onChange={(e) => setEmpresaForm({ ...empresaForm, rut: e.target.value })}
                                placeholder="76.123.456-7"
                                className="input-industrial"
                                data-testid="empresa-rut"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="input-label">Subdominio</Label>
                            <Input
                                value={empresaForm.subdominio}
                                onChange={(e) => setEmpresaForm({ ...empresaForm, subdominio: e.target.value.toLowerCase() })}
                                placeholder="miempresa"
                                className="input-industrial"
                                data-testid="empresa-subdominio"
                            />
                            <p className="text-xs text-muted-foreground">
                                {empresaForm.subdominio || 'miempresa'}.multisistemas.cl
                            </p>
                        </div>
                        <div className="space-y-2">
                            <Label className="input-label">Color Principal</Label>
                            <div className="flex gap-3">
                                <Input
                                    type="color"
                                    value={empresaForm.primary_color}
                                    onChange={(e) => setEmpresaForm({ ...empresaForm, primary_color: e.target.value })}
                                    className="w-16 h-12 p-1 cursor-pointer"
                                />
                                <Input
                                    value={empresaForm.primary_color}
                                    onChange={(e) => setEmpresaForm({ ...empresaForm, primary_color: e.target.value })}
                                    className="input-industrial font-mono"
                                />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowEmpresaDialog(false)} className="border-2">
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleCreateEmpresa}
                            disabled={processing}
                            className="btn-industrial bg-primary text-primary-foreground"
                            data-testid="save-empresa-btn"
                        >
                            {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Crear Empresa'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Create Usuario Dialog */}
            <Dialog open={showUsuarioDialog} onOpenChange={setShowUsuarioDialog}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-heading uppercase">Nuevo Usuario</DialogTitle>
                        <DialogDescription>Complete los datos del nuevo usuario</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label className="input-label">Nombre</Label>
                            <Input
                                value={usuarioForm.nombre}
                                onChange={(e) => setUsuarioForm({ ...usuarioForm, nombre: e.target.value })}
                                placeholder="Nombre completo"
                                className="input-industrial"
                                data-testid="usuario-nombre"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="input-label">Email</Label>
                            <Input
                                type="email"
                                value={usuarioForm.email}
                                onChange={(e) => setUsuarioForm({ ...usuarioForm, email: e.target.value })}
                                placeholder="correo@empresa.com"
                                className="input-industrial"
                                data-testid="usuario-email"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="input-label">Contraseña</Label>
                            <Input
                                type="password"
                                value={usuarioForm.password}
                                onChange={(e) => setUsuarioForm({ ...usuarioForm, password: e.target.value })}
                                placeholder="••••••••"
                                className="input-industrial"
                                data-testid="usuario-password"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="input-label">Rol</Label>
                            <Select
                                value={usuarioForm.rol}
                                onValueChange={(v) => setUsuarioForm({ ...usuarioForm, rol: v })}
                            >
                                <SelectTrigger className="input-industrial" data-testid="usuario-rol">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="admin">Administrador</SelectItem>
                                    <SelectItem value="solicitante">Solicitante</SelectItem>
                                    <SelectItem value="gerente">Gerente</SelectItem>
                                    <SelectItem value="seguridad">Seguridad</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label className="input-label">Empresa</Label>
                            <Select
                                value={usuarioForm.empresa_id}
                                onValueChange={(v) => setUsuarioForm({ ...usuarioForm, empresa_id: v })}
                            >
                                <SelectTrigger className="input-industrial" data-testid="usuario-empresa">
                                    <SelectValue placeholder="Seleccionar empresa" />
                                </SelectTrigger>
                                <SelectContent>
                                    {empresas.map(e => (
                                        <SelectItem key={e.id} value={e.id}>{e.nombre}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowUsuarioDialog(false)} className="border-2">
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleCreateUsuario}
                            disabled={processing}
                            className="btn-industrial bg-primary text-primary-foreground"
                            data-testid="save-usuario-btn"
                        >
                            {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Crear Usuario'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Assign Modules Dialog */}
            <Dialog open={showModulosDialog} onOpenChange={setShowModulosDialog}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-heading uppercase">Asignar Módulos</DialogTitle>
                        <DialogDescription>{selectedEmpresa?.nombre}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-4">
                        {modulos.map(modulo => (
                            <div
                                key={modulo.codigo}
                                className={`flex items-center gap-3 p-3 rounded-sm border-2 cursor-pointer transition-colors ${
                                    modulosSeleccionados.includes(modulo.codigo)
                                        ? 'border-primary bg-primary/10'
                                        : 'border-border hover:border-muted-foreground'
                                }`}
                                onClick={() => toggleModulo(modulo.codigo)}
                                data-testid={`modulo-checkbox-${modulo.codigo}`}
                            >
                                <Checkbox
                                    checked={modulosSeleccionados.includes(modulo.codigo)}
                                    onCheckedChange={() => toggleModulo(modulo.codigo)}
                                />
                                <div className="flex-1">
                                    <p className="font-medium">{modulo.nombre}</p>
                                    <p className="text-xs text-muted-foreground">{modulo.descripcion}</p>
                                </div>
                                {modulosSeleccionados.includes(modulo.codigo) && (
                                    <CheckCircle className="h-5 w-5 text-primary" />
                                )}
                            </div>
                        ))}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowModulosDialog(false)} className="border-2">
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleAsignarModulos}
                            disabled={processing}
                            className="btn-industrial bg-primary text-primary-foreground"
                            data-testid="save-modulos-btn"
                        >
                            {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
